import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import {
  getFormattedFullAddress,
  getIsEmptyObj,
} from 'app/pages/loanApplication/LoanApplication.utils';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import {
  ADDRESS_COUNTRY_TYPE,
  HousingType,
  MaritalStatusType,
  SpouseRelationTypeEnum,
} from 'constants/loanApplication';
import { PendingChangesActionType } from 'constants/pendingChanges';
import {
  formatPhoneNumberForView,
  getConditionalArray,
  getConditionalObject,
  normalizeRequestPhoneNumber,
  replaceEmptyWithNull,
} from 'utils/data';
import { formatCalendarDate } from 'utils/date';
import { omitMissedInConfigFields } from 'utils/form';
import { jsonStringify } from 'utils/json';
import {
  clearEntitySideDataFromEmptyData,
  getAddressData,
  getInvalidTabsValidationMessage,
} from 'utils/loanApplication';

import { showMessageAction } from 'store/global/actions';

import {
  BORROWER_TYPE,
  BORROWERS_SECTIONS_EXTRACT_PENDING_CHANGES_ACTION,
  BORROWERS_SECTIONS_NAMES,
  BORROWING_ENTITY_TABS_HREF,
  ENTITY_TYPE_HREF,
  NEW_ENTITY,
} from '../EditEntityModal.constants';

import {
  clearFieldsBeforeDispatch,
  getFormattedFormDataByDeclarationType,
  normalizeReceivedDeclarations,
} from './MainContent/Borrower/Declarations/Declarations.utils';
import { CREDIT_EVENT_FORM_KEYS } from './MainContent/Borrower/Declarations/Finances/Finances.constants';
import { getCreditEventDatesToSend } from './MainContent/Borrower/Declarations/Finances/Finances.utils';
import { formatPoaForForm, formatPoaForRequest } from './MainContent/Borrower/POA/POA.utils';

const RESIDENCE_TYPE = {
  CURRENT_RESIDENCE: 'CurrentResidence',
  PREVIOUS_RESIDENCE: 'PreviousResidence',
};

const getResidenceValues = residence => {
  const { type, isSubjectProperty, ownershipType, id } = residence;
  const address = _.get(residence, 'address.address', {});
  const addressId = _.get(residence, 'address.id', '');
  const addressData = getAddressData({ ...address, addressId });

  return {
    id,
    type: type.type,
    isSubjectProperty,
    housingType: ownershipType?.type,
    monthlyRentAmount: ownershipType?.rentAmount,
    startOfResidence: type.startOfResidence
      ? formatCalendarDate(type.startOfResidence, COMMON_REQUEST_DATE_FORMAT)
      : type.startOfResidence,
    endOfResidence: type.endOfResidence
      ? formatCalendarDate(type.endOfResidence, COMMON_REQUEST_DATE_FORMAT)
      : type.endOfResidence,
    monthlyRelativesPartRentAmount: ownershipType?.partOfRentAmount,
    hasRelativesPartRent: !_.isNil(ownershipType?.partOfRentAmount),
    fullAddress: getFormattedFullAddress({
      address: addressData,
    }),
    ...addressData,
  };
};

const getResidencesData = residences =>
  residences.reduce(
    (acc, residence) => {
      const residenceValues = getResidenceValues(residence);

      if (residenceValues.type === RESIDENCE_TYPE.CURRENT_RESIDENCE) {
        acc.currentResidence = residenceValues;

        return acc;
      }

      acc.previousResidences.push(residenceValues);

      return acc;
    },
    {
      currentResidence: null,
      previousResidences: [],
    },
  );

export const normalizeBorrowerApplicationData = borrowers =>
  borrowers.reduce((acc, data) => {
    if (data.id === NEW_ENTITY) {
      acc[data.id] = data;

      return acc;
    }

    const {
      dependents,
      cellPhone,
      homePhone,
      workPhone,
      positiveRentalHistory = '',
      caivrsNumber = null,
      caivrsAuthorizationCode = null,
      residences,
      mailingAddress,
      declarations,
      type,
      suffix,
      id,
      spouse,
    } = data;
    const isPrimaryBorrower = type === BORROWER_TYPE.PRIMARY_BORROWER;

    const { agesOfDependents, count } = dependents ?? {};

    const formattedDeclarations = normalizeReceivedDeclarations(declarations);
    const formattedAgesOfDependents = agesOfDependents?.reduce((acc, value, index) => {
      acc[`agesOfDependents${index}`] = value;

      return acc;
    }, {});

    const { currentResidence, previousResidences } = getResidencesData(residences);
    const isMailingAddressTheSameAsCurrentAddress = !mailingAddress;
    const mailingAddressData = isMailingAddressTheSameAsCurrentAddress
      ? { country: ADDRESS_COUNTRY_TYPE.USA }
      : getAddressData({ ...mailingAddress.address, addressId: mailingAddress.id });
    const isMarried = data.maritalStatus === MaritalStatusType.MARRIED;

    const formattedData = {
      ...data,
      declarations: formattedDeclarations,
      dependents: {
        count,
        agesOfDependents: formattedAgesOfDependents,
      },
      cellPhone: formatPhoneNumberForView(cellPhone),
      homePhone: formatPhoneNumberForView(homePhone),
      workPhone: formatPhoneNumberForView(workPhone),
      creditReporting: {
        positiveRentalHistory,
        caivrsNumber,
        caivrsAuthorizationCode,
      },
      isMailingAddressTheSameAsCurrentAddress,
      mailingAddress: mailingAddressData,
      isPrimaryBorrower,
      currentResidence,
      previousResidences,
      suffix: suffix ?? null,
      relation: isMarried && spouse ? spouse.type : null,
      spouseFirstName: spouse?.firstName,
      spouseLastName: spouse?.lastName,
      spouseEmail: spouse?.email,
      ...getConditionalObject(!!data.powerOfAttorney, {
        powerOfAttorney: formatPoaForForm(data.powerOfAttorney),
      }),
    };

    acc[id] = formattedData;

    return acc;
  }, {});

export const normalizeNBEApplicationData = nonBorrowers =>
  nonBorrowers.reduce((acc, data) => {
    if (data.id === NEW_ENTITY) {
      acc[data.id] = {
        ownershipInterest: false,
        ...data,
      };

      return acc;
    }

    const { cellPhoneNumber, id, spouse } = data;
    const address = _.get(data, 'address.address', {});
    const addressData = getAddressData(address);
    const isMarried = data.maritalStatus === MaritalStatusType.MARRIED;

    const formattedData = {
      ...data,
      relation: isMarried && spouse ? spouse.type : null,
      spouseFirstName: spouse?.firstName,
      spouseLastName: spouse?.lastName,
      spouseEmail: spouse?.email,
      cellPhone: formatPhoneNumberForView(cellPhoneNumber),
      address: addressData,
      powerOfAttorney: {
        name: data.poaName,
        verbiage: data.poaVerbiage,
        designation: data.poaDesignation ?? null,
      },
      // remove after BE fix
      obsoleteRelation: data.relation,
    };

    acc[id] = formattedData;

    return acc;
  }, {});

export const formatPersonalInfoForm = borrowerData => {
  const {
    firstName,
    lastName,
    middleName,
    suffix,
    dateOfBirth,
    citizenshipStatus,
    hasItin,
    dependents,
    email,
    isFirstTimeHomeBuyer,
    maritalStatus,
    ssn,
    cellPhone,
    homePhone,
    workPhone,
    workPhoneNumberExtension,
    isLiveTogetherForTwoYears,
    relation,
    spouseFirstName,
    spouseLastName,
    spouseEmail,
  } = borrowerData;

  if (_.isUndefined(firstName)) {
    return;
  }

  const spouse =
    maritalStatus?.value === MaritalStatusType.MARRIED && !!relation
      ? {
          type: relation?.value,
          ...getConditionalObject(relation?.value === SpouseRelationTypeEnum.Other, {
            firstName: spouseFirstName,
            lastName: spouseLastName,
            email: spouseEmail,
          }),
        }
      : null;

  return replaceEmptyWithNull({
    isLiveTogetherForTwoYears,
    firstName,
    lastName,
    middleName,
    suffix: suffix?.value,
    dateOfBirth: dayjs(dateOfBirth).format(COMMON_REQUEST_DATE_FORMAT),
    citizenshipStatus: citizenshipStatus?.value,
    hasItin: hasItin ?? null,
    dependents: dependents?.count?.value
      ? {
          agesOfDependents: Object.values(dependents.agesOfDependents ?? {}),
          count: dependents.count.value,
        }
      : null,
    email,
    isFirstTimeHomeBuyer: isFirstTimeHomeBuyer?.value,
    maritalStatus: maritalStatus?.value,
    ssn,
    cellPhone: normalizeRequestPhoneNumber(cellPhone),
    homePhone,
    workPhone,
    workPhoneNumberExtension,
    spouse,
  });
};

const getAddressValuesToSend = data => {
  const county = data.county?.value || data.county;
  const country = data.country?.value || data.country;
  const state = data.state?.value || data.state;

  return {
    id: data.addressId,
    county,
    country,
    state,
    zip: data.zip,
    city: data.city,
    street: data.street,
    unit: data.unit,
    isOutsideUsa: data.isOutsideUsa || false,
  };
};

const getBorrowerAddressInfoValuesToSend = ({
  currentResidence,
  currentResidenceId,
  currentResidenceAddressId,
  previousResidences = [],
  mailingAddressInfo,
  isLiveTogetherForTwoYears,
}) => {
  if (isLiveTogetherForTwoYears) {
    return {
      mailingAddress: null,
      mailingAddressIsTheSameAsCurrentAddress: true,
      residences: [],
    };
  }

  if (!currentResidence) {
    return;
  }

  const residences = [currentResidence, ...previousResidences].reduce((acc, residence, index) => {
    if (
      residence.isDeleted ||
      residence.pendingChangesActionType === PendingChangesActionType.DELETED
    ) {
      return acc;
    }

    const isCurrentResidence = index === 0;
    const id = isCurrentResidence ? currentResidenceId : residence.id;
    const addressId = isCurrentResidence ? currentResidenceAddressId : residence.addressId;
    const housingType = residence.housingType?.value || residence.housingType;
    const isRent = housingType === HousingType.RENT;
    const isLivingWithRelative = housingType === HousingType.LIVING_WITH_RELATIVE;
    const address = getAddressValuesToSend({ ...residence, addressId });

    acc.push({
      address,
      housingType,
      rent: isRent ? residence.monthlyRentAmount : undefined,
      partOfRentAmount:
        isLivingWithRelative && residence.hasRelativesPartRent
          ? residence.monthlyRelativesPartRentAmount
          : undefined,
      startOfResidence: dayjs(residence.startOfResidence).format(COMMON_REQUEST_DATE_FORMAT),
      endOfResidence: residence.endOfResidence
        ? dayjs(residence.endOfResidence).format(COMMON_REQUEST_DATE_FORMAT)
        : undefined,
      isSubjectProperty: residence.isSubjectProperty ?? null,
      ...getConditionalObject(!residence.isAdded, {
        id,
      }),
    });

    return acc;
  }, []);

  const mailingAddress = mailingAddressInfo
    ? getAddressValuesToSend(mailingAddressInfo)
    : undefined;

  return {
    residences,
    mailingAddress,
    mailingAddressIsTheSameAsCurrentAddress: !mailingAddress,
  };
};

// The loan officer's event dates are saved through the borrower patch's creditEventDates group.
const omitCreditEventDates = declarations =>
  declarations?.finances
    ? { ...declarations, finances: _.omit(declarations.finances, CREDIT_EVENT_FORM_KEYS) }
    : declarations;

export const getBorrowerValuesToSend = (
  form,
  applicationData,
  isBplLoanPurposeType,
  entitiesSideData,
) => {
  return Object.entries(form).reduce(
    (acc, [borrowerId, borrowerData]) => {
      const {
        powerOfAttorney,
        alternateNames,
        currentResidence,
        mailingAddress,
        creditReporting,
        declarations,
        isMailingAddressTheSameAsCurrentAddress,
        isLiveTogetherForTwoYears,
      } = borrowerData;
      const entitySideData = entitiesSideData[borrowerId];
      const { previousAddresses } = entitySideData || {};

      const formattedAlternateNames = alternateNames?.reduce((acc, group) => {
        if (!getIsEmptyObj(group)) {
          acc.push(
            replaceEmptyWithNull({
              ...group,
              suffix: group.suffix ? group.suffix.value : null,
            }),
          );
        }

        return acc;
      }, []);

      const formattedDeclarations = getFormattedFormDataByDeclarationType(declarations);

      const clearedFormattedDeclarations = omitCreditEventDates(
        clearFieldsBeforeDispatch({
          declarations: formattedDeclarations,
          isBplLoanPurposeType,
        }),
      );
      const creditEventDates = getCreditEventDatesToSend({
        finances: declarations?.finances,
        storedFinances: applicationData[borrowerId]?.declarations?.finances,
      });

      const { currentResidence: currentResidenceData, mailingAddress: mailingAddressData } =
        applicationData[borrowerId];

      const addressInfo = getBorrowerAddressInfoValuesToSend({
        isLiveTogetherForTwoYears,
        currentResidence,
        currentResidenceId: currentResidenceData?.id || null,
        currentResidenceAddressId: currentResidenceData?.addressId || null,
        previousResidences: previousAddresses,
        mailingAddressInfo: isMailingAddressTheSameAsCurrentAddress
          ? null
          : {
              ...mailingAddress,
              addressId: mailingAddressData?.addressId || null,
            },
      });

      const formattedBorrowerData = {
        id: borrowerId,
        personalInformation: formatPersonalInfoForm(borrowerData),
        ...getConditionalObject(powerOfAttorney, {
          powerOfAttorney: formatPoaForRequest(powerOfAttorney),
        }),
        alternateNames: formattedAlternateNames,
        addressInfo,
        creditReporting: creditReporting && replaceEmptyWithNull(creditReporting),
        declarations: declarations && clearedFormattedDeclarations,
        ...getConditionalObject(creditEventDates, { creditEventDates }),
      };

      if (borrowerId === NEW_ENTITY) {
        acc.create.push(formattedBorrowerData);
      }

      if (borrowerId !== NEW_ENTITY) {
        acc.update.push(formattedBorrowerData);
      }

      return acc;
    },
    { update: [], create: [] },
  );
};

export const getNBEValuesToSend = (form, applicationData) => {
  return Object.entries(form).reduce(
    (
      acc,
      [
        nonBorrowerEntityId,
        { relation, spouseFirstName, spouseLastName, spouseEmail, powerOfAttorney, ...nbeData },
      ],
    ) => {
      const spouse =
        nbeData.maritalStatus?.value === MaritalStatusType.MARRIED && !!relation
          ? {
              type: relation?.value,
              ...getConditionalObject(relation?.value === SpouseRelationTypeEnum.Other, {
                firstName: spouseFirstName,
                lastName: spouseLastName,
                email: spouseEmail,
              }),
            }
          : null;

      const formattedNBEData = {
        id: nonBorrowerEntityId,
        ...nbeData,
        socialSecurityNumber: nbeData.ssn,
        dateOfBirth: nbeData.dateOfBirth
          ? dayjs(nbeData.dateOfBirth).format(COMMON_REQUEST_DATE_FORMAT)
          : null,
        suffix: nbeData.suffix?.value,
        cellPhoneNumber: normalizeRequestPhoneNumber(nbeData.cellPhone),
        address: {
          ...nbeData.address,
          state: nbeData.address.state?.value,
          county: nbeData.address.county?.value,
        },
        spouse,
        maritalStatus: nbeData.maritalStatus?.value,
        ownershipInterest: nbeData.ownershipInterest,
        fhaEligible: nbeData.fhaEligible?.value ?? nbeData.fhaEligible ?? null,
        poaName: powerOfAttorney?.name,
        poaVerbiage: powerOfAttorney?.verbiage,
        requestMetadata: { tabId: LoanApplicationTabId.PERSONAL_INFORMATION },
        // remove after BE fix
        ...getConditionalObject(!!applicationData[nonBorrowerEntityId]?.obsoleteRelation, {
          relation: applicationData[nonBorrowerEntityId].obsoleteRelation,
        }),
      };

      acc.push(formattedNBEData);

      return acc;
    },
    [],
  );
};

export const getLoadDataActionParams = ({ applicationId, entityId, subtab }) => {
  switch (subtab) {
    case ENTITY_TYPE_HREF.BORROWER:
      return {
        applicationId,
        borrowerId: entityId,
        params: {
          includes: 'residences',
        },
      };

    case ENTITY_TYPE_HREF.NON_BORROWER:
      return {
        applicationId,
        nonBorrowerEntityId: entityId,
      };

    default:
      return {
        applicationId,
        borrowerId: entityId,
      };
  }
};

export const getEntityRequestParams = ({ applicationId, body, isCreateNBEFlow }) => {
  if (!isCreateNBEFlow) {
    return {
      applicationId,
      body,
    };
  }

  const data = body[0];
  const { id, ...restData } = data;

  return {
    applicationId,
    ...restData,
  };
};

const mergeValuesCustomizer = (objValue, srcValue) => {
  if (_.isArray(srcValue)) {
    return srcValue;
  }
};

export const mergeValues = ({ form, initialValues, fieldsNames }) => {
  const entitiesIds = Object.keys(initialValues);

  const mergedForm = entitiesIds.reduce((acc, entityId) => {
    const clonedEntityInitialValues = _.cloneDeep(initialValues[entityId]);
    const mergedValues = _.mergeWith(
      clonedEntityInitialValues,
      form[entityId],
      mergeValuesCustomizer,
    );

    acc[entityId] = mergedValues;

    return acc;
  }, {});

  return omitMissedInConfigFields({ form: mergedForm, fieldsNames });
};

export const clearEntitiesSideDataFromEmptyData = entitiesSideData => {
  return _.mapValues(entitiesSideData, entitySideData =>
    clearEntitySideDataFromEmptyData(entitySideData),
  );
};

export const handleMissingRequiredFields = ({
  validationErrors,
  entityId,
  isBorrowerFlow,
  section,
  dispatch,
  setInvalidTabs,
  setInvalidHeadTabs,
  entitiesData,
  hasInvalidCustomFields = false,
}) => {
  const entityErrors = _.get(validationErrors, entityId, {});
  const errorKeys = _.keys(entityErrors);

  const sectionsMap = isBorrowerFlow ? BORROWERS_SECTIONS_EXTRACT_PENDING_CHANGES_ACTION : {};
  const invalidSections = _.pickBy(sectionsMap, fields =>
    _.some(fields(entityErrors), (__, field) => errorKeys.some(key => key === field)),
  );

  const baseInvalidTabKeys = isBorrowerFlow
    ? _.keys(invalidSections)
    : getConditionalArray(errorKeys.length, [section]);
  const invalidTabKeys =
    hasInvalidCustomFields && !baseInvalidTabKeys.includes(BORROWING_ENTITY_TABS_HREF.CUSTOM_FIELDS)
      ? [...baseInvalidTabKeys, BORROWING_ENTITY_TABS_HREF.CUSTOM_FIELDS]
      : baseInvalidTabKeys;

  if (isBorrowerFlow && baseInvalidTabKeys.length && !invalidSections[section]) {
    const tabs = _.values(_.pick(BORROWERS_SECTIONS_NAMES, baseInvalidTabKeys));
    const message = getInvalidTabsValidationMessage(tabs);

    dispatch(showMessageAction({ message, variant: 'error', id: jsonStringify(tabs) }));
  }

  setInvalidTabs(invalidTabKeys);

  const otherEntityIds = _.keys(_.omit(validationErrors, entityId)).filter(
    id => !_.isEmpty(validationErrors[id]),
  );

  setInvalidHeadTabs(otherEntityIds);

  if (otherEntityIds.length && !baseInvalidTabKeys.length) {
    const entityNames = otherEntityIds
      .map(id => {
        const entity = entitiesData.find(e => (e.value ?? e.id) === id);

        return entity?.label ?? entity?.fullName;
      })
      .filter(Boolean);

    const entityType = isBorrowerFlow ? 'borrowers' : 'non borrowing entities';
    const message = `Check validation for other ${entityType} (${entityNames.join(', ')})`;

    dispatch(showMessageAction({ message, variant: 'error', id: jsonStringify(otherEntityIds) }));
  }
};
